// brain.ts — the AI client. Calls the LIVE Oriora server brain for this app:
//   https://api.orioralabs.com/api/apps/mood_tracker/reflect-week
// with the customer's Oriora session token. The server holds the prompt + bills the
// Oriora wallet (Managed AI / BYOK). Mood data is stored on-device; the reflect/ask
// calls send only a DERIVED-SIGNALS object (tag↔score correlations, day-of-week
// pattern, window shift, volatility, multi-week trajectory — see signals.rs). Raw
// notes never enter the signals payload (chat messages the user types are sent as-is,
// like any chat; Oriora retains none of it).
//
// HARDEST guardrail (enforced server-side, mirrored in our UI copy): the reflection
// REFLECTS the computed structure only — it never advises, diagnoses, or suggests
// actions. We do not rebuild the brain; we compute the signals, call it, render the result.

import { invoke } from "./tauri-web";
import { getAccessToken } from "./supabase";
import type { MoodSignals } from "./store";

const SERVER = "https://api.orioralabs.com";

export type BrainErrorCode = "insufficient_funds" | "managed_ai_blocked" | "unauthenticated" | "unavailable" | "no_store" | "too_large";
export class BrainError extends Error {
  code: BrainErrorCode;
  constructor(code: BrainErrorCode, message: string) {
    super(message);
    this.code = code;
    this.name = "BrainError";
  }
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

// SSE reader for the streaming fleet chat (request body {stream:true} → text/event-stream):
// `: open` / `: keepalive` comments and [DONE] are ignored, each `data: {"chunk":…}` event
// feeds onChunk, and the `data: {"done":true, …}` event carries the exact old JSON payload —
// returned where res.json() used to be, so all existing action/reply mapping stays untouched.
async function readSseChat(bodyStream: ReadableStream<Uint8Array>, onChunk: (t: string) => void): Promise<Record<string, unknown>> {
  const reader = bodyStream.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: Record<string, unknown> | null = null;
  for (;;) {
    const { value, done: eof } = await reader.read();
    if (eof) break;
    buf += decoder.decode(value, { stream: true });
    let nl: number;
    while ((nl = buf.indexOf("\n")) !== -1) {
      const line = buf.slice(0, nl).replace(/\r$/, "");
      buf = buf.slice(nl + 1);
      if (!line.startsWith("data:")) continue; // comment (": open"/": keepalive") or blank line
      const payload = line.slice(5).trim();
      if (payload === "[DONE]") continue;
      let ev: Record<string, unknown>;
      try { ev = JSON.parse(payload) as Record<string, unknown>; } catch { continue; }
      if (typeof ev.chunk === "string") onChunk(ev.chunk);
      else if (ev.done) done = ev;
    }
  }
  if (!done) throw new BrainError("unavailable", "The reply stream ended early — please try again.");
  // A streamed reply can end with a billing/auth error instead of a payload ({done:true, error, _status}).
  // Returning it as data dissolved the "Top up" message into an empty action (found live 2026-08-27) —
  // throw the same BrainError the buffered path would.
  if (typeof done.error === "string" && done.error) {
    const st = typeof done._status === "number" ? done._status : 0;
    const msg = typeof done.message === "string" && done.message ? done.message : done.error;
    if (st === 401) throw new BrainError("unauthenticated", msg);
    if (st === 402) throw new BrainError("insufficient_funds", msg);
    if (st === 403) throw new BrainError("managed_ai_blocked", msg);
    throw new BrainError("unavailable", msg);
  }
  return done;
}

export async function brainCall<T>(endpoint: string, body: unknown, onChunk?: (t: string) => void): Promise<T> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  // Retry transient failures (502/503/504 + network). Hard errors (401/402/403) never retry.
  const MAX = 3;
  for (let attempt = 1; attempt <= MAX; attempt++) {
    let res: Response;
    try {
      res = await fetch(`${SERVER}/api/apps/mood_tracker/${endpoint}`, {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${token}` },
        body: JSON.stringify(onChunk && body && typeof body === "object" ? { ...(body as Record<string, unknown>), stream: true } : body),
      });
    } catch {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is temporarily unavailable.");
    }
    if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
    if (res.status === 402) throw new BrainError("insufficient_funds", "Add funds to keep using AI.");
    if (res.status === 403) throw new BrainError("managed_ai_blocked", (await res.json().catch(() => null))?.message || "Connect a vendor key in Oriora settings to use AI for this app.");
    if ([502, 503, 504].includes(res.status)) {
      if (attempt < MAX) { await sleep(attempt * 800); continue; }
      throw new BrainError("unavailable", "AI is busy — please try again.");
    }
    if (!res.ok) throw new BrainError("unavailable", "AI request failed.");
    // Streaming fleet chat: with onChunk the body carries stream:true and the server answers
    // as SSE — read it incrementally (readSseChat above); the done event IS the old JSON
    // payload. Anything that is not an event stream (old server, an error status body, a
    // proxy that buffered it away) falls through to the buffered path — never a dead end.
    if (onChunk && res.body && (res.headers.get("content-type") ?? "").includes("text/event-stream")) {
      return (await readSseChat(res.body, onChunk)) as unknown as T;
    }
    return (await res.json()) as T;
  }
  throw new BrainError("unavailable", "AI is temporarily unavailable.");
}

export interface WeekReflection {
  reflection: string;
}

/**
 * Weekly mood-pattern reflection. The ONE AI feature.
 *
 * What changed (v2): we no longer ship the raw entries for the brain to NARRATE.
 * Instead we compute the non-obvious STRUCTURE on-device (tag↔score correlations,
 * day-of-week pattern, current-vs-prior window shift, volatility, multi-week
 * trajectory — see store.ts / signals.rs) and send ONLY those small aggregates.
 * The brain then surfaces specific, named-number observations the user couldn't
 * spot at a glance. Raw notes never enter the signals payload.
 *
 * The endpoint + Bearer JWT are unchanged: POST /api/apps/mood_tracker/reflect-week.
 * The server prompt must be updated to read the `signals` object (operator task);
 * it stays OBSERVATIONAL ONLY — describes patterns/deltas, never advises or diagnoses.
 */
export async function reflectWeek(signals: MoodSignals): Promise<WeekReflection> {
  if (signals.overall.total_entries === 0) {
    throw new BrainError("unavailable", "Log a few moods first — there's nothing to reflect on yet.");
  }
  const r = await brainCall<Partial<WeekReflection>>("reflect-week", { signals });
  return {
    reflection: String(r.reflection ?? "").trim(),
  };
}


export interface MoodAnswer { answer: string; }

/** Ask a free-form question about your OWN mood log. Observational only — same server guardrails. */
export async function askMoods(question: string, signals: MoodSignals): Promise<MoodAnswer> {
  const q = question.trim();
  if (!q) throw new BrainError("unavailable", "Type a question first.");
  if (signals.overall.total_entries === 0) {
    throw new BrainError("unavailable", "Log a few moods first — there's nothing to answer from yet.");
  }
  const r = await brainCall<Partial<MoodAnswer>>("ask", { question: q, signals });
  return { answer: String(r.answer ?? "").trim() };
}

// Login opens the ecosystem web login INSIDE the app (its own WebKit window), not the OS
// browser — the in-app https handback is robust on iOS where the custom-scheme deep-link is
// fragile (mirrors expense_audit_ai). Session returns via auth:deep-link (App.tsx + ipc.rs).
// The web callback must allowlist `mood-tracker` as a return target (server-side, operator task).
// ── Cloud backup (the user owns their data) — fleet standard ────────────────────
export interface CloudBackupState { connected: boolean; provider?: string; data?: unknown }
async function authedFetch(endpoint: string, init?: RequestInit): Promise<Response> {
  const token = await getAccessToken();
  if (!token) throw new BrainError("unauthenticated", "Please sign in to Oriora.");
  return fetch(`${SERVER}/api/apps/mood_tracker/${endpoint}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${token}` },
  });
}
/** Save the local backup envelope to the user's connected cloud store (GitHub/Drive). */
export async function cloudBackupSave(envelope: unknown): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("backup", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ data: envelope }) });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to back up to the cloud.");
  // 413 = over the standard path's size limit (photo-heavy backup). Not retryable — the caller
  // switches to the device-direct GitHub save, or tells the customer how to enable it.
  if (res.status === 413) throw new BrainError("too_large", "This backup is too large for the standard path.");
  if (!res.ok) throw new BrainError("unavailable", "Cloud backup failed — please try again.");
  return (await res.json()) as { provider: string };
}
/** Save arbitrary text — a note the user dictates, or the whole conversation — to the user's OWN
 *  cloud store (their GitHub/Drive). Honors an explicit destination when the user names one. Oriora
 *  retains nothing (text relay only). Throws no_store when nothing is connected. */
export async function cloudStoreSave(
  content: string,
  opts?: { name?: string; kind?: string; destination?: "github" | "gdrive" },
): Promise<{ provider: string }> {
  let res: Response;
  try {
    res = await authedFetch("store", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ content, name: opts?.name, kind: opts?.kind, provider: opts?.destination }),
    });
  } catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", "Connect GitHub or Google Drive in your Oriora account to save to the cloud.");
  if (!res.ok) throw new BrainError("unavailable", "Saving to your cloud failed — please try again.");
  return (await res.json()) as { provider: string };
}

/** Read the cloud backup for restore. `connected:false` when no store is connected.
 *  Pass `provider` to read a NAMED source (the RESTORE STORY's explicit restore) — the server
 *  409s rather than silently reading a different cloud than the one the customer asked for. */
export async function cloudBackupLoad(provider?: "github" | "gdrive"): Promise<CloudBackupState> {
  let res: Response;
  try { res = await authedFetch(provider ? `backup?provider=${provider}` : "backup"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (res.status === 409) throw new BrainError("no_store", `${provider === "gdrive" ? "Google Drive" : "GitHub"} isn't connected in your Oriora account.`);
  if (!res.ok) throw new BrainError("unavailable", "Couldn't read your cloud backup.");
  return (await res.json()) as CloudBackupState;
}

/** The RESTORE STORY status line — which clouds are connected, which one backups WRITE to, and the
 *  dated backup each holds. Ground truth from the relay (CUSTOMER-CLOUD-STORAGE.md §THE RESTORE STORY). */
export interface CloudBackupSlot { backup_exists: boolean; updated_at: string | null; bytes: number | null }
export interface CloudBackupStatusInfo {
  connected: ("github" | "gdrive")[];
  writes_to: "github" | "gdrive" | null;
  slots: Partial<Record<"github" | "gdrive", CloudBackupSlot>>;
}
export async function cloudBackupStatus(): Promise<CloudBackupStatusInfo> {
  let res: Response;
  try { res = await authedFetch("backup/status"); }
  catch { throw new BrainError("unavailable", "Couldn't reach Oriora — check your connection."); }
  if (res.status === 401) throw new BrainError("unauthenticated", "Your session expired — sign in again.");
  if (!res.ok) throw new BrainError("unavailable", "Couldn't check your backup status.");
  return (await res.json()) as CloudBackupStatusInfo;
}

export const openWebLogin = () => invoke<void>("open_login_window");
